import React, { Component, Fragment } from 'react'
import { connect } from 'react-redux'
import { push } from 'react-router-redux'
import compose from 'lodash/fp/compose'

import prepareComponent from 'client/shared/decorators/prepare-component'
import errorBoundary, {
  ErrorBoundaryProps,
} from 'client/shared/decorators/error-boundary'
import withMarketing, {
  MarketingProps,
} from 'client/shared/decorators/with-marketing'
import urlFor, { QueryParams } from 'shared/tools/url-helper'
import { serverRedirectTo } from 'client/shared/helpers/redirect-helpers'

import { show } from 'client/shared/reducers/popup-reducer'

import {
  USER_BOOKS_READING,
  buildUser,
  loadQuotes,
  loadImpressions,
  loadFollowers,
} from 'client/bookmate/reducers/user-reducer'

import { getImpressionsByIds } from 'client/bookmate/reducers/impressions-reducer'

import { getQuotesByIds } from 'client/bookmate/reducers/quotes-reducer'

import { getLibraryCardsByIds } from 'client/bookmate/reducers/library-cards-reducer'

import HeaderBox from 'client/shared/boxes/header-box'
import UserSubHeaderBox from 'client/bookmate/boxes/user-sub-header-box'
import UserInfoBox from 'client/bookmate/boxes/user-info-box'

import Spacer from 'client/shared/blocks/spacer'
import List from 'client/bookmate/blocks/list'
import { LayoutColumn } from 'client/shared/blocks/layout-column'

import { CardQuoteBox } from 'client/bookmate/boxes/card-quote-box'
import { CardImpressionBox } from 'client/bookmate/boxes/card-impression-box'

import { Dispatch, State } from 'shared/types/redux'
import { Params } from 'client/shared/types/react-router'
import { UserCombined } from 'client/shared/types/user'
import { QuoteProps } from 'client/shared/types/quote'
import { ImpressionProps } from 'client/shared/types/impression'
import type { Exact } from 'types/local/exact-check'

type Props = (Exact<MarketingProps> & {
  dispatch: Dispatch
  params: Params
  user: UserCombined
  quotes: QuoteProps[]
  impressions: ImpressionProps[]
  auth: string
  query?: QueryParams
}) &
  ErrorBoundaryProps

class UserIndexBox extends Component<Props> {
  componentDidUpdate() {
    const {
      dispatch,
      impressions,
      query,
      params: { username },
      user: { login, myself },
    } = this.props

    if (!impressions.length) {
      window.location.replace(urlFor.userBooksAll(username, query))
    }

    if (username === login && myself) {
      dispatch(push(urlFor.userBooksAll(login, query)))
    }
  }

  render() {
    const {
      user,
      user: { login, myself },
      quotes,
      impressions,
      dispatch,
      auth,
      query,
    } = this.props
    const interceptor = auth ? null : () => dispatch(show())

    return (
      <Fragment>
        <HeaderBox />
        <UserInfoBox user={user} />
        <UserSubHeaderBox hidden={myself} />
        <LayoutColumn>
          <Spacer size={32} />
          {/* previously, we showed user's books here */}
          {Boolean(impressions.length) && (
            <Fragment>
              <List
                header="book.impressions_header"
                allLink={urlFor.userImpressions(login, query)}
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                allLinkInterceptor={interceptor}
              >
                {impressions.slice(0, 3).map((impression, i) => (
                  <CardImpressionBox
                    impression={impression}
                    embedded={impression.resource}
                    key={i}
                  />
                ))}
              </List>
              <Spacer size={56} />
            </Fragment>
          )}
          {Boolean(quotes.length) && (
            <Fragment>
              <List
                header="book.quotes_header"
                allLink={urlFor.userQuotes(login, query)}
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                allLinkInterceptor={interceptor}
              >
                {quotes.slice(0, 3).map(quote => (
                  <CardQuoteBox
                    quote={quote}
                    key={quote.uuid}
                    embeddedBook={quote.book}
                  />
                ))}
              </List>
            </Fragment>
          )}
          <Spacer size="bottom-page" />
        </LayoutColumn>
      </Fragment>
    )
  }
}

const prepareWrapper = prepareComponent(
  async function ({ store, params, location }) {
    const { username } = params
    const { dispatch } = store

    const actions = [
      dispatch(loadQuotes(username, 1, 20)),
      dispatch(loadImpressions({ username, page: 1, per_page: 20 })),
      dispatch(loadFollowers(username)),
    ]
    await Promise.all(actions)

    if (!store.getState().user.impressions.length) {
      serverRedirectTo(urlFor.userBooksAll(username, location.query))
    }
  },
  {
    updateParams: 'username',
    userListener: true,
  },
)

const connectWrapper = connect((state: State) => ({
  user: buildUser(state, state.user.login),
  impressions: getImpressionsByIds(state, state.user.impressions),
  quotes: getQuotesByIds(state, state.user.quotes),
  libraryCards: getLibraryCardsByIds(
    state,
    state.user.books[USER_BOOKS_READING],
  ),
  auth: state.currentUser.auth,
  query: state.app.storedQuery,
}))

const wrappers = compose(
  prepareWrapper,
  connectWrapper,
  withMarketing,
  errorBoundary,
)

export default wrappers(UserIndexBox)
